import type { JSX } from 'react';
import { Center, Loader } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ApiError, api } from './api';
import { LoginPage } from './core/LoginPage';
import { Shell } from './core/Shell';
import { ProjectsPage } from './core/ProjectsPage';
import { ProjectIndex, ProjectLayout } from './core/ProjectLayout';
import { SchedulePage } from './modules/scheduling/SchedulePage';
import { AccountPage } from './core/AccountPage';
import { UsersPage } from './core/UsersPage';
import { ProjectTeamPage } from './core/ProjectTeamPage';
import { SnapshotsPage } from './core/SnapshotsPage';
import { ProjectDataPage } from './modules/projectdata/ProjectDataPage';
import { TimePage } from './modules/projectdata/TimePage';
import { FilesPage } from './modules/projectdata/FilesPage';
import { TimeOverviewPage } from './modules/projectdata/TimeOverviewPage';
import { CostsOverviewPage } from './modules/costs/CostsOverviewPage';
import { ScheduleOverviewPage } from './modules/scheduling/ScheduleOverviewPage';
import { SchedulePrintPage } from './modules/scheduling/SchedulePrintPage';
import { TasksPage } from './modules/scheduling/TasksPage';
import { CalendarPage } from './modules/scheduling/CalendarPage';
import { MappePage } from './export/MappePage';
import { CostsPage } from './modules/costs/CostsPage';
import { TenderingPage } from './modules/tendering/TenderingPage';
import { LibrariesPage } from './core/LibrariesPage';
import { SettingsPage } from './core/SettingsPage';
import { OffersPage } from './modules/offers/OffersPage';
import { OfferPage } from './modules/offers/OfferPage';

/**
 * Decides between login screen and application by asking the server who we
 * are. The session cookie is the single source of truth; nothing about the
 * login state is kept in the browser.
 *
 * Routes are project-centred: the start page is the project list, and each
 * project's areas live below /projekte/:id/.
 */
export function App(): JSX.Element {
  // Invitation and password links work signed in or not, so they are decided
  // before the question of who is signed in.
  if (window.location.pathname === '/konto') return <AccountPage />;
  return <SignedInApp />;
}

function SignedInApp(): JSX.Element {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });

  if (me.isPending) {
    return (
      <Center h="100dvh">
        <Loader />
      </Center>
    );
  }
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) return <LoginPage />;
    throw me.error;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell user={me.data} />}>
          <Route path="/" element={<ProjectsPage />} />
          <Route path="/projekte" element={<Navigate to="/" replace />} />
          <Route path="/projekte/:projectId" element={<ProjectLayout />}>
            <Route index element={<ProjectIndex />} />
            <Route path="termine" element={<SchedulePage />} />
            <Route path="termine/druck" element={<SchedulePrintPage />} />
            <Route path="aufgaben" element={<ProjectScoped view={TasksPage} />} />
            <Route path="kalender" element={<ProjectScoped view={CalendarPage} />} />
            <Route path="kosten" element={<CostsPage />} />
            <Route path="ausschreibung" element={<TenderingPage stage="ausschreibung" />} />
            <Route path="vergabe" element={<TenderingPage stage="vergabe" />} />
            <Route path="projekt" element={<ProjectDataPage />} />
            <Route path="zeiten" element={<TimePage />} />
            <Route path="dateien" element={<FilesPage />} />
            <Route path="team" element={<ProjectTeamPage />} />
            <Route path="snapshots" element={<SnapshotsPage />} />
          </Route>
          {me.data.permissions.includes('projectdata.read') && <Route path="/zeiten" element={<TimeOverviewPage />} />}
          {me.data.permissions.includes('costs.read') && <Route path="/kosten" element={<CostsOverviewPage />} />}
          {me.data.permissions.includes('scheduling.read') && <Route path="/termine" element={<ScheduleOverviewPage />} />}
          {me.data.permissions.includes('scheduling.read') && <Route path="/aufgaben" element={<TasksPage />} />}
          {me.data.permissions.includes('scheduling.read') && <Route path="/kalender" element={<CalendarPage />} />}
          <Route path="/mappe" element={<MappePage />} />
          {me.data.permissions.includes('offers.read') && (
            <>
              <Route path="/angebote" element={<OffersPage />} />
              <Route path="/angebote/beteiligte" element={<OffersPage />} />
              <Route path="/angebote/referenzen" element={<OffersPage />} />
              <Route path="/angebote/:offerId/:tab?" element={<OfferPage />} />
            </>
          )}
          <Route path="/bibliotheken/:tab?" element={<LibrariesPage permissions={me.data.permissions} />} />
          {me.data.permissions.includes('users.manage') && (
            <Route path="/benutzer" element={<UsersPage />} />
          )}
          {me.data.permissions.includes('users.manage') && (
            <Route path="/einstellungen/:tab?" element={<SettingsPage />} />
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

/** A cross-project view shown inside a project, limited to that project. */
function ProjectScoped({ view: View }: { view: (props: { projectId?: string }) => JSX.Element }): JSX.Element {
  const { projectId = '' } = useParams();
  return <View projectId={projectId} />;
}

